/**
 * OnboardingTour — 4-Karten-Tour beim ersten Mandant-Login.
 *
 * Zeigt einen modalen Overlay mit nacheinander vier Inhaltskarten:
 *   1. Willkommen + AVGS-§-45-Hinweis
 *   2. Module — Reihenfolge frei wählbar, jederzeit pausierbar
 *   3. Dokumente — BWA + andere Dokumente
 *   4. Datenschutz — DSGVO-Rechte + Link zur Datenschutzerklärung
 *
 * Steuerung: Weiter / Zurück / Tour überspringen. Nach Abschluss wird im
 * `localStorage` ein Marker (`mandant.onboarding.completed`) gesetzt — die
 * Tour erscheint dann nicht mehr von selbst. „Einführung erneut anzeigen"
 * im Footer der Mandant-Home öffnet sie wieder (über `forceOpen`-Prop).
 *
 * A11y:
 *   - role="dialog" + aria-modal + aria-labelledby
 *   - ESC schließt (gilt als „Skip", localStorage wird trotzdem gesetzt,
 *     damit der User nicht beim nächsten Login wieder belästigt wird)
 *   - Pfeiltasten ←/→ navigieren zwischen Karten
 *   - Enter triggert „Weiter" / „Verstanden"
 *   - Fokus initial auf den primären Button
 *
 * Kein Portal — der Modal sitzt im normalen DOM-Flow als fixed-overlay,
 * weil wir keine externen Dependencies einführen wollen und das Mandanten-
 * Layout keine konkurrierenden z-index-Inseln hat.
 */

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { de } from '../../lib/i18n/de';
import { useFocusTrap } from '../a11y/useFocusTrap';

export const ONBOARDING_STORAGE_KEY = 'mandant.onboarding.completed';

interface OnboardingTourProps {
  /**
   * Bei `true` wird der Modal sofort gezeigt — unabhängig vom localStorage-
   * Marker. Wird vom „Einführung erneut anzeigen"-Link verwendet.
   */
  readonly forceOpen?: boolean;
  /** Wird gerufen, wenn der Modal geschlossen wird (sowohl Abschluss als auch Skip). */
  readonly onClose: () => void;
  /**
   * Optional: Case-ID für den DSGVO-Self-Service-Link auf Karte 4. Wenn
   * gesetzt, wird zusätzlich zum allgemeinen Datenschutz-Link ein direkter
   * Link auf `/mandant/case/{caseId}/datenschutz` angezeigt.
   */
  readonly caseId?: string;
}

interface TourCard {
  readonly eyebrow: string;
  readonly title: string;
  readonly body: string;
  /** Pastell-Akzentfarbe (CSS-Variable) für den Header-Block. */
  readonly accentVar: string;
  /** Optional: extra Footer-Element (z.B. Datenschutz-Link in Karte 4). */
  readonly footerSlot?: React.ReactNode;
}

const TOTAL_STEPS = 4;

export function OnboardingTour({ forceOpen = false, onClose, caseId }: OnboardingTourProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);
  const primaryButtonRef = useRef<HTMLButtonElement>(null);

  // WCAG 2.1 AA: Focus-Trap im Modal (verhindert Tab in den Hintergrund).
  useFocusTrap(dialogRef, { active: true, initialFocusRef: primaryButtonRef });

  // Fokus auf primären Button beim Step-Wechsel (Initial-Fokus übernimmt
  // useFocusTrap).
  useEffect(() => {
    primaryButtonRef.current?.focus();
  }, [stepIndex]);

  // Body-Scroll sperren, solange die Tour offen ist (klassisches Modal-Pattern).
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  void forceOpen; // forceOpen steuert nur, OB die Tour gemountet wird (im Parent)

  const cards: ReadonlyArray<TourCard> = [
    {
      eyebrow: de.mandant.onboarding.step1Eyebrow,
      title: de.mandant.onboarding.step1Title,
      body: de.mandant.onboarding.step1Body,
      accentVar: 'var(--color-dim-private)',
    },
    {
      eyebrow: de.mandant.onboarding.step2Eyebrow,
      title: de.mandant.onboarding.step2Title,
      body: de.mandant.onboarding.step2Body,
      accentVar: 'var(--color-dim-identity)',
    },
    {
      eyebrow: de.mandant.onboarding.step3Eyebrow,
      title: de.mandant.onboarding.step3Title,
      body: de.mandant.onboarding.step3Body,
      accentVar: 'var(--color-dim-resources)',
    },
    {
      eyebrow: de.mandant.onboarding.step4Eyebrow,
      title: de.mandant.onboarding.step4Title,
      body: de.mandant.onboarding.step4Body,
      accentVar: 'var(--color-dim-style)',
      footerSlot: (
        <div className="flex flex-col items-start gap-2">
          <Link
            to="/datenschutz"
            className="inline-flex items-center gap-1 font-sans text-xs font-medium uppercase tracking-widest text-stone-700 underline hover:text-stone-900"
          >
            {de.mandant.onboarding.step4PrivacyLink}
          </Link>
          {caseId && (
            <Link
              to={`/mandant/case/${caseId}/datenschutz`}
              className="inline-flex items-center gap-1 font-sans text-xs font-medium uppercase tracking-widest text-stone-700 underline hover:text-stone-900"
            >
              Meine DSGVO-Rechte verwalten →
            </Link>
          )}
        </div>
      ),
    },
  ];

  const card = cards[stepIndex] ?? cards[0]!;
  const isLast = stepIndex === TOTAL_STEPS - 1;

  const finish = () => {
    try {
      window.localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    } catch {
      // Private-Mode oder Storage-Quota — kein Drama, dann gibt's halt
      // nochmal eine Tour beim nächsten Login. Kein Telemetrie-Pfad.
    }
    onClose();
  };

  const goNext = () => {
    if (isLast) {
      finish();
      return;
    }
    setStepIndex((i) => Math.min(i + 1, TOTAL_STEPS - 1));
  };

  const goPrev = () => {
    setStepIndex((i) => Math.max(i - 1, 0));
  };

  const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      finish();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goNext();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goPrev();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      goNext();
    }
  };

  return (
    <div
      // fixed-overlay; bewusst kein Portal, siehe Datei-Header
      className="fixed inset-0 z-50 flex items-end justify-center bg-stone-900/40 px-4 pb-4 pt-6 backdrop-blur-sm md:items-center md:p-8"
      onClick={(e) => {
        // Klick auf den Backdrop = Skip
        if (e.target === e.currentTarget) finish();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
        tabIndex={-1}
        onKeyDown={handleKey}
        className="relative w-full max-w-lg overflow-hidden rounded-sm border border-stone-200 bg-white shadow-xl"
      >
        {/* Header — Pastell-Akzentblock + Eyebrow */}
        <header className="relative px-6 pb-4 pt-8 md:px-8 md:pb-5 md:pt-10">
          <div
            aria-hidden="true"
            className="absolute right-6 top-6 h-12 w-12 rounded-sm md:right-8 md:top-8 md:h-16 md:w-16"
            style={{ backgroundColor: card.accentVar, opacity: 0.4 }}
          />
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            {de.mandant.onboarding.progress
              .replace('{current}', (stepIndex + 1).toString())
              .replace('{total}', TOTAL_STEPS.toString())}{' '}
            · {card.eyebrow}
          </p>
          <h2
            id="onboarding-title"
            className="mt-3 max-w-[18ch] font-serif text-3xl font-black leading-tight tracking-tight text-stone-900 md:text-4xl"
          >
            {card.title}
          </h2>

          <button
            type="button"
            onClick={finish}
            aria-label={de.mandant.onboarding.skip}
            className="absolute right-2 top-2 inline-flex h-10 w-10 items-center justify-center rounded-full text-stone-500 hover:bg-stone-100 hover:text-stone-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </header>

        {/* Body */}
        <div className="px-6 pb-2 md:px-8">
          <p className="font-serif text-base leading-relaxed text-stone-700 md:text-[17px]">
            {card.body}
          </p>
          {card.footerSlot && <div className="mt-4">{card.footerSlot}</div>}
        </div>

        {/* Step-Indikator */}
        <div className="px-6 pt-6 md:px-8" aria-hidden="true">
          <div className="flex gap-2">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <span
                key={i}
                className="h-1 flex-1 rounded-sm transition-colors"
                style={{
                  backgroundColor:
                    i <= stepIndex ? '#1c1917' /* stone-900 */ : '#e7e5e4' /* stone-200 */,
                }}
              />
            ))}
          </div>
        </div>

        {/* Footer-Buttons */}
        <footer className="mt-4 flex flex-col-reverse items-stretch gap-3 border-t border-stone-100 px-6 py-5 md:flex-row md:items-center md:justify-between md:px-8">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={finish}
              className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500 underline-offset-2 hover:text-stone-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
            >
              {de.mandant.onboarding.skip}
            </button>
          </div>
          <div className="flex items-center gap-3 md:justify-end">
            {stepIndex > 0 && (
              <button
                type="button"
                onClick={goPrev}
                className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-stone-200 bg-white px-5 py-2 font-sans text-xs font-medium uppercase tracking-widest text-stone-700 hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
              >
                ← {de.mandant.onboarding.back}
              </button>
            )}
            <button
              ref={primaryButtonRef}
              type="button"
              onClick={goNext}
              className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-sm bg-stone-900 px-6 py-2 font-sans text-xs font-medium uppercase tracking-widest text-white hover:bg-stone-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2 md:flex-initial"
            >
              {isLast ? de.mandant.onboarding.finish : `${de.mandant.onboarding.next} →`}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

/**
 * Hilfs-Hook für die Mandant-Home: liest beim Mount, ob die Tour schon einmal
 * absolviert wurde. Liefert true beim allerersten Besuch (oder wenn der Marker
 * gelöscht wurde).
 *
 * SSR-safe: läuft nur clientseitig (Lazy-init im useState-Default).
 */
export function shouldShowOnboardingByDefault(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(ONBOARDING_STORAGE_KEY) !== 'true';
  } catch {
    // Private-Mode etc. — wir zeigen die Tour einmal pro Session.
    return true;
  }
}
